import * as React from "react";

import {
  Input as SaltInput,
  type InputProps as SaltInputProps,
} from "@/design-system/salt";

export type InputVariant = "default" | "auth" | "inline" | "ghost";

type SaltTextAlign = NonNullable<SaltInputProps["textAlign"]>;

const SALT_TEXT_ALIGNMENTS: readonly string[] = ["left", "center", "right"];

/**
 * Salt renders the inner `<input>` with `text-align: var(--input-textAlign, left)`, so a
 * `text-align` declaration on the wrapper never reaches the text. Alignment must travel through
 * Salt's `textAlign` prop — the same contract `CurrencyInput` and `PercentageInput` already use.
 */
function splitTextAlign(style?: React.CSSProperties): {
  textAlign?: SaltTextAlign;
  style?: React.CSSProperties;
} {
  if (!style || style.textAlign === undefined) return { style };

  const { textAlign, ...rest } = style;
  if (typeof textAlign === "string" && SALT_TEXT_ALIGNMENTS.includes(textAlign)) {
    return { textAlign: textAlign as SaltTextAlign, style: rest };
  }

  return { style };
}

export interface InputProps extends React.ComponentPropsWithoutRef<"input"> {
  endAdornment?: SaltInputProps["endAdornment"];
  startAdornment?: SaltInputProps["startAdornment"];
  variant?: InputVariant;
  style?: React.CSSProperties;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      defaultValue,
      disabled,
      endAdornment,
      id,
      placeholder,
      readOnly,
      role,
      style,
      startAdornment,
      type,
      value,
      variant = "default",
      ...inputProps
    },
    ref,
  ) => {
    const isTertiary = variant === "inline" || variant === "ghost";
    const isFileInput = type === "file";
    const ariaInvalid = inputProps["aria-invalid"];
    const { textAlign, style: rootStyle } = splitTextAlign(style);

    return (
      <SaltInput
        bordered={!isTertiary}
        className={className}
        defaultValue={isFileInput ? undefined : defaultValue}
        disabled={disabled}
        emptyReadOnlyMarker=""
        endAdornment={endAdornment}
        id={id}
        inputProps={{
          ...inputProps,
          type,
        }}
        inputRef={ref}
        placeholder={placeholder}
        readOnly={readOnly}
        role={role}
        style={{ width: "100%", ...rootStyle }}
        startAdornment={startAdornment}
        textAlign={textAlign}
        validationStatus={
          ariaInvalid === true || ariaInvalid === "true" ? "error" : undefined
        }
        value={isFileInput ? "" : value}
        variant={isTertiary ? "tertiary" : "primary"}
      />
    );
  },
);
Input.displayName = "Input";

export { Input };
